import { FC, FormEvent, ReactElement, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Typography,
} from "@mui/material";

import { Checkbox, TextInput } from "@/components";
import { useSnackbar } from "@/hooks";
import { useLoginMutation } from "@/services";
import { app_logo_full } from "@/constants";
import { isValidEmail } from "@/utils/form-validation";

const Login: FC = (): ReactElement => {
  const [login, { isLoading: loading }] = useLoginMutation();

  const navigate = useNavigate();
  const { show } = useSnackbar();
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [remember, setRemember] = useState<boolean>(false);

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!isValidEmail(email)) {
      show("Invalid email address!", "Please enter a valid email.", "error");
      return;
    }
    login({ email, password })
      .unwrap()
      .catch((error) => {
        show("Invalid email or password!", error.message, "error");
      });
  };

  return (
    <Stack
      direction={"column"}
      justifyContent={"center"}
      alignItems={"center"}
      height={"100%"}
    >
      <Box sx={{ width: { xs: "95%", sm: "70%" }, p: { xs: 2, md: 0 } }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={1}>
            <Box>
              <Box sx={{display:{xs:"flex",lg:"none"}}} mb={1.5}>
                <img src={app_logo_full} alt="" width={152} />
              </Box>
              <Typography fontSize={14} color={"neutral.500"}>
                Sign in to your account
              </Typography>
            </Box>
          </Stack>
          <Box sx={{ my: 2 }}>
            <Stack spacing={2}>
              <Box>
                <TextInput
                  label="Email"
                  type="email"
                  placeholder="hi@example.com"
                  color="success"
                  error={false}
                  helperText={""}
                  value={email}
                  handleChangeValue={(val: string) => setEmail(val)}
                  inputProps={{ "data-testid": "login-email" }}
                />
              </Box>
              <Box>
                <TextInput
                  label="Password"
                  placeholder="*************"
                  color="success"
                  type={"password"}
                  error={false}
                  helperText={""}
                  value={password}
                  handleChangeValue={(val: string) => setPassword(val)}
                  inputProps={{ "data-testid": "login-password" }}
                />
              </Box>
              <Stack
                direction={"row"}
                alignItems={"center"}
                justifyContent={"space-between"}
                mt={3}
              >
                <Checkbox
                  label="Remember Me"
                  color="primary"
                  checked={remember}
                  onChange={() => setRemember(!remember)}
                />
                <Typography
                  variant="subtitle2"
                  fontSize={14}
                  color="primary"
                  sx={{ cursor: "pointer", fontWeight: 500 }}
                  onClick={() => navigate("/forgot-password")}
                >
                  Forgot Password
                </Typography>
              </Stack>
            </Stack>
          </Box>
          <Button
            variant="contained"
            fullWidth
            sx={{
              mt: 2,
              py: 1.5,
              fontSize: "16px",
              fontWeight: 500,
            }}
            disabled={!isValidEmail(email) || password === "" || loading}
            type="submit"
            color="primary"
            startIcon={loading ? <CircularProgress size={22} /> : null}
            data-testid="login-submit"
          >
            {loading ? "Loading" : "Login"}
          </Button>
        </form>
      </Box>
    </Stack>
  );
};

export default Login;
